import { Divider, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import Button from "components/basic/button";
import Card from "components/basic/card";
import { headerOffset } from "components/basic/header";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import {
  useLoginMutation,
  useGoogleLoginMutation,
  useMeQuery,
} from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { checkRequired, toErrorMap, validateEmail } from "pages/formTools";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignInData } from "../..";
import { useGoogleLogin } from "@react-oauth/google";
import GoogleIcon from "@mui/icons-material/Google";

interface LoginProps {
  signInData: SignInData;
  setSignInData: React.Dispatch<React.SetStateAction<SignInData>>;
}

const SignInForm: React.FC<LoginProps> = ({ signInData, setSignInData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("auth");
  const showSnack = useSnack();
  const [{ data: loggedInUser, fetching }] = useMeQuery();

  const handleHomePageRedirect = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // If user is already logged in and somehow they happen upon this page
  // Then send them back to landing page
  useEffect(() => {
    if (fetching) {
      return;
    }

    if (!loggedInUser?.me) {
      return;
    }

    handleHomePageRedirect();
  }, [fetching, handleHomePageRedirect, loggedInUser]);

  const [, login] = useLoginMutation();
  const [, googleLoginMutation] = useGoogleLoginMutation();

  const googleSignIn = useGoogleLogin({
    // Otherwise Google silently reuses the last account, leaving people with
    // several Google accounts no way to pick another.
    prompt: "select_account",
    onSuccess: async (tokenResponse) => {
      const result = await googleLoginMutation(
        { accessToken: tokenResponse.access_token },
        { requestPolicy: "network-only" },
      );

      if (result.data?.googleLogin.errors) {
        showSnack(
          result.data.googleLogin.errors[0].message ??
            t("sign_in.google_failed"),
          "error",
        );
        return;
      }

      showSnack(t("sign_in.google_success"), "success");
      handleHomePageRedirect();
    },
    onError: (error) =>
      showSnack(t("sign_in.google_failed_detail", { error }), "error"),
  });

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({
    email: null,
    password: null,
  });

  const handleSubmit = useCallback(async () => {
    Object.entries(signInData).forEach(([name, value]) => {
      switch (name) {
        case "email": {
          const error = validateEmail({ name, value, required: true });

          if (error) {
            setErrors((prev) => ({ ...prev, [name]: error }));
            return;
          }

          break;
        }
        case "password": {
          const error = checkRequired(name, value);

          if (error) {
            setErrors((prev) => ({ ...prev, [name]: error }));
            return;
          }

          break;
        }
        default:
          break;
      }
    });

    const response = await login(
      {
        options: {
          email: signInData.email,
          password: signInData.password,
        },
      },
      { requestPolicy: "network-only" },
    );

    if (response.data?.login.errors) {
      setErrors(toErrorMap(response.data.login.errors));
      return;
    }

    handleHomePageRedirect();
    showSnack(t("sign_in.success"), "success");
  }, [signInData, login, handleHomePageRedirect, showSnack, t]);

  const handleChange = useCallback(
    (name: string, value: string) => {
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }

      setSignInData((prev) => ({ ...prev, [name]: value }));
    },
    [errors, setSignInData],
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        height: `calc(100vh - ${headerOffset(1)})`,
        padding: `0 ${theme.spacing(2)}`,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: theme.spacing(2),
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "550px",
        }}
      >
        <div style={{ width: "100%" }}>
          <Card disableVerticalPadding square dark>
            <div
              id="right section"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(2),
                maxWidth: "800px",
                padding: `0 ${theme.spacing(4)}`,
              }}
            >
              <Typography
                variant="h4"
                align="center"
                style={{ margin: `${theme.spacing(1.5)} 0` }}
              >
                {t("sign_in.title")}
              </Typography>

              <TextField
                size="tiny"
                name="email"
                type="email"
                value={signInData.email}
                placeHolder={t("sign_in.email_placeholder")}
                onChange={({ target: { name, value } }) =>
                  handleChange(name, value)
                }
                {...(errors?.email ? { error: errors.email } : {})}
              />

              <TextField
                size="tiny"
                name="password"
                type="password"
                value={signInData.password}
                placeHolder={t("sign_in.password_placeholder")}
                onChange={({ target: { name, value } }) =>
                  handleChange(name, value)
                }
                {...(errors?.password ? { error: errors.password } : {})}
              />
            </div>

            <div
              style={{
                padding: `0 ${theme.spacing(4)}`,
              }}
            >
              <div style={{ marginBottom: theme.spacing(2) }}>
                <Button
                  onClick={() => navigate("?currentPage=forgotPassword")}
                  variant="text"
                  size="small"
                  style={{ color: "orange" }}
                >
                  {t("sign_in.forgot")}
                </Button>
              </div>

              <Button
                onClick={handleSubmit}
                variant="contained"
                color="warning"
                size="small"
                style={{ color: theme.palette.common.white }}
                fullWidth
              >
                {t("common.sign_in")}
              </Button>

              <Divider
                sx={{
                  borderColor: theme.palette.common.white,
                  margin: `${theme.spacing(3)} 0`,
                }}
              />

              <Button
                onClick={() => navigate("/signup")}
                variant="text"
                size="small"
                style={{ color: theme.palette.common.white }}
              >
                <RichText
                  t={t}
                  i18nKey="common.no_account"
                  components={{
                    a: (
                      <span
                        style={{
                          color: "orange",
                          marginLeft: theme.spacing(1),
                        }}
                      />
                    ),
                  }}
                />
              </Button>

              <Divider
                sx={{
                  borderColor: theme.palette.common.white,
                  margin: `${theme.spacing(1)} 0`,
                }}
              />

              <Button
                onClick={() => googleSignIn()}
                variant="contained"
                size="small"
                startAdornment={<GoogleIcon />}
                fullWidth
              >
                {t("sign_in.with_google")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignInForm;
